import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, fns } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow } from "../components/Bits.jsx";

/** The Pod Steward console: facts computed by code, agenda drafted by machine, crit run by you. */
export default function Pods() {
  const s = useSession();
  const [pods, setPods] = useState([]);
  const [briefs, setBriefs] = useState({});
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!s.profile?.cohortId) return;
    const clauses = [where("cohortId", "==", s.profile.cohortId)];
    return onSnapshot(query(collection(db, "pods"), ...clauses), (snap) =>
      setPods(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => s.role !== "master" || !s.profile?.clusterId || p.clusterId === s.profile.clusterId)));
  }, [s.profile?.cohortId, s.profile?.clusterId, s.role]);

  useEffect(() => onSnapshot(collection(db, "stewards"), (snap) => {
    const m = {}; snap.forEach((d) => (m[d.id] = d.data())); setBriefs(m);
  }), []);

  const run = async (podId) => {
    setBusy(podId); setMsg(null);
    try {
      const call = httpsCallable(fns, "podStewardBrief");
      await call({ podId });
    } catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };

  const pair = async () => {
    setBusy("pair"); setMsg(null);
    try {
      const call = httpsCallable(fns, "pairStrangerTests");
      const r = await call({ cohortId: s.profile.cohortId });
      setMsg(`Paired: ${r.data.pairs} pairs, ${r.data.crossPod} cross-pod${r.data.leftover ? ", one resident joins a pair as a second stranger" : ""}. Residents see their partner on the Help tab.`);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };

  return (
    <div style={{ maxWidth: 940 }}>
      <div className="row between wrapflex">
        <div>
          <Eyebrow>The Pod Steward</Eyebrow>
          <h1 style={{ fontSize: 24, marginTop: 4 }}>Walk into the crit already knowing the room</h1>
        </div>
        {s.isSenior && (
          <button className="btn ghost" disabled={busy === "pair"} onClick={pair}>
            {busy === "pair" ? "Pairing…" : "Pair Stranger Tests (cohort)"}
          </button>
        )}
      </div>
      <p style={{ color: "var(--graphite)", maxWidth: "68ch" }}>
        The Steward computes the facts in code — who has gone quiet, whose work bounced, who never filed —
        and drafts an agenda from them. The flags are deterministic and cannot be hallucinated. You run the
        crit; the Steward just carries your clipboard.
      </p>
      {msg && <div className="card" style={{ margin: "12px 0" }}>{msg}</div>}

      {pods.map((p) => {
        const b = briefs[p.id];
        return (
          <div key={p.id} className="card" style={{ marginTop: 12 }}>
            <div className="row between wrapflex">
              <div>
                <strong>{p.name ?? p.id}</strong>{" "}
                <span className="pill">{(p.memberUids ?? []).length} residents</span>{" "}
                {b && <span className="pill">briefed {b.generatedAt?.toDate?.().toLocaleDateString?.() ?? ""}</span>}
              </div>
              <button className="btn ghost" disabled={busy === p.id} onClick={() => run(p.id)}>
                {busy === p.id ? "Drafting…" : b ? "Refresh brief" : "Draft crit brief"}
              </button>
            </div>

            {b && (
              <div style={{ marginTop: 12, borderTop: "1px solid var(--rule)", paddingTop: 12 }}>
                {b.flags?.length > 0 && (
                  <div className="warn" style={{ marginBottom: 12 }}>
                    <Eyebrow style={{ color: "var(--madder)" }}>Needs a human this week</Eyebrow>
                    {b.flags.map((f, i) => (
                      <p key={i} style={{ fontSize: 14, margin: "6px 0 0" }}>
                        <strong>{f.name}:</strong> {f.signal}. <em>{f.action}</em>
                      </p>
                    ))}
                  </div>
                )}
                {b.agenda?.openingNote && <p style={{ fontStyle: "italic", fontSize: 15 }}>"{b.agenda.openingNote}"</p>}
                <table style={{ marginTop: 8 }}>
                  <thead><tr><th>Order</th><th>Min</th><th>Suggested charge</th></tr></thead>
                  <tbody>
                    {(b.agenda?.order ?? []).map((o, i) => (
                      <tr key={i}><td>{o.name}</td><td className="mono">{o.minutes}</td><td style={{ fontSize: 14 }}>{o.suggestedCharge}</td></tr>
                    ))}
                  </tbody>
                </table>
                {b.agenda?.themes?.length > 0 && (
                  <p style={{ fontSize: 14, marginTop: 8 }}><strong>Themes:</strong> {b.agenda.themes.join(" · ")}</p>
                )}
              </div>
            )}
          </div>
        );
      })}
      {!pods.length && <p style={{ color: "var(--graphite)", fontStyle: "italic", marginTop: 16 }}>No pods in your view yet.</p>}
    </div>
  );
}
